import { useState, useEffect } from "react";
import { api } from "@/App";
import StaffLayout from "@/components/layout/StaffLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertCircle, Clock, CheckCircle, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";

const StaffIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateData, setUpdateData] = useState({ status: "", staff_notes: "" });

  useEffect(() => {
    fetchIssues();
  }, [filter]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? "/issues" : `/issues?status=${filter}`;
      const response = await api.get(url);
      setIssues(response.data);
    } catch (error) {
      toast.error("Failed to load issues");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedIssue || !updateData.status) return;
    
    setUpdating(true);
    try {
      await api.put(`/staff/issues/${selectedIssue.id}?issue_status=${updateData.status}${updateData.staff_notes ? `&staff_notes=${encodeURIComponent(updateData.staff_notes)}` : ''}`);
      toast.success("Issue updated");
      setSelectedIssue(null);
      setUpdateData({ status: "", staff_notes: "" });
      fetchIssues();
    } catch (error) {
      toast.error("Failed to update issue");
    } finally {
      setUpdating(false);
    }
  };

  const openUpdateDialog = (issue) => {
    setSelectedIssue(issue);
    setUpdateData({ status: issue.status, staff_notes: issue.staff_notes || "" });
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'open':
        return <span className="badge badge-warning"><Clock size={12} className="mr-1" /> Open</span>;
      case 'in_progress':
        return <span className="badge badge-gold"><AlertCircle size={12} className="mr-1" /> In Progress</span>;
      case 'resolved':
        return <span className="badge badge-success"><CheckCircle size={12} className="mr-1" /> Resolved</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <StaffLayout title="Issues">
      <div className="space-y-6 animate-fade-in">
        {/* Filter */}
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-[#131316] border-[#2A2A2F] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131316] border-[#2A2A2F]">
              <SelectItem value="all" className="text-white">All Issues</SelectItem>
              <SelectItem value="open" className="text-white">Open</SelectItem>
              <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
              <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[#A0A0A0] text-sm">{issues.length} issues</span>
        </div>

        {/* Issues List */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="h-32 rounded-xl animate-shimmer" />
            ))}
          </div>
        ) : issues.length > 0 ? (
          <div className="space-y-4">
            {issues.map((issue) => (
              <Card key={issue.id} className="bg-[#131316] border-[#2A2A2F]" data-testid={`staff-issue-${issue.id}`}>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-white font-semibold">{issue.subject}</h3>
                      <p className="text-[#6B6B6B] text-sm">User: {issue.user_id} • {formatDate(issue.created_at)}</p>
                    </div>
                    {getStatusBadge(issue.status)}
                  </div>
                  <p className="text-[#A0A0A0] text-sm mb-4">{issue.description}</p>
                  
                  {issue.staff_notes && (
                    <div className="bg-[#0A0A0B] rounded-lg p-3 mb-4">
                      <p className="text-[#D4AF37] text-xs font-medium mb-1">Staff Notes:</p>
                      <p className="text-[#A0A0A0] text-sm">{issue.staff_notes}</p>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => openUpdateDialog(issue)}
                    variant="outline"
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0B]"
                    data-testid={`update-issue-btn-${issue.id}`}
                  >
                    <MessageSquare size={16} className="mr-2" /> Update Status
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <AlertCircle size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
            <h3 className="text-white text-xl font-semibold mb-2">No Issues Found</h3>
            <p className="text-[#A0A0A0]">No issues match your filter</p>
          </div>
        )}

        {/* Update Dialog */}
        <Dialog open={!!selectedIssue} onOpenChange={() => setSelectedIssue(null)}>
          <DialogContent className="bg-[#131316] border-[#2A2A2F] text-white">
            <DialogHeader>
              <DialogTitle>Update Issue</DialogTitle>
            </DialogHeader>
            
            {selectedIssue && (
              <div className="space-y-4">
                <div className="bg-[#0A0A0B] rounded-lg p-4">
                  <p className="text-white font-medium">{selectedIssue.subject}</p>
                  <p className="text-[#6B6B6B] text-sm mt-1">{selectedIssue.description}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[#A0A0A0] text-sm">Status</label>
                  <Select
                    value={updateData.status}
                    onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
                  >
                    <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1E] border-[#2A2A2F]">
                      <SelectItem value="open" className="text-white">Open</SelectItem>
                      <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
                      <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[#A0A0A0] text-sm">Staff Notes</label>
                  <Textarea
                    value={updateData.staff_notes}
                    onChange={(e) => setUpdateData({ ...updateData, staff_notes: e.target.value })}
                    placeholder="Add notes or response..."
                    rows={3}
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37] resize-none"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setSelectedIssue(null)}
                className="border-[#2A2A2F] text-white hover:bg-[#1A1A1E]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={updating}
                className="bg-[#D4AF37] text-[#0A0A0B]"
                data-testid="save-issue-btn"
              >
                {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </StaffLayout>
  );
};

export default StaffIssues;
